import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';

import { blockHeading } from '../panelHeading';
import { STATUS_PILL_HUES, statusPill, statusPillLabel, StatusPillTone } from '../statusPill';

export type StatusBadgeTone = StatusPillTone;

/** A labelled line under the pill — `Effective Date` / `14 Mar 2023`. */
export interface IStatusBadgeDetail {
  label: string;
  value: string;
}

export interface IStatusBadgeWithReasonProps {
  /** The field label above the pill — `VALUE BAND`, `CURRENT TIER`. Drawn uppercase by the rule. */
  label?: string;
  /**
   * How the frame cases that label. `3583-10457` draws the profile's `VALUE BAND` / `CURRENT TIER`
   * uppercase and tracked, but `3583-7541` draws `Contactability Status` in sentence case — so the
   * block-label rule is the default and a card that measures its own label opts out.
   */
  labelCase?: 'upper' | 'sentence';
  /** The status itself, pre-translated: `Active`, `High Value`, `Bronze`. */
  status: string;
  tone?: StatusBadgeTone;
  /**
   * Overrides the tone hue for a colour the token set carries no role for — `3583-10121` inks the
   * current-tier pill `#CD7F32`, a tier metal rather than a status tone.
   */
  color?: string;
  /** When the status took effect. */
  effective?: IStatusBadgeDetail;
  /**
   * The value this status replaced. The catalogue's current/prior tier format is this component with
   * the slot filled; the generic status badge is the same component with it empty.
   */
  prior?: IStatusBadgeDetail;
  /**
   * Why the status holds — `Governing reason` / `Frequency cap reached`. Drawn stacked and in the
   * pill's own hue, not as one of the split rows `effective` and `prior` take.
   */
  reason?: IStatusBadgeDetail;
  className?: string;
}

const DEFAULT_TONE: StatusBadgeTone = 'neutral';

const Wrapper = styled.div`
  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

const FieldLabel = styled.h4<{ $sentence: boolean }>`
  ${blockHeading}

  /* The content scope colours h1-h6 through a descendant rule, so the opt-out needs the same doubled
     class blockHeading uses to land its own hue. */
  && {
    ${({ $sentence }) =>
      $sentence
        ? `
    letter-spacing: normal;
    text-transform: none;
  `
        : ''}
  }
`;

const Pill = styled.span<{ $hue: string }>`
  ${statusPill}
`;

const PillLabel = styled.span`
  ${statusPillLabel}
`;

const DetailList = styled.dl`
  margin: 0;

  > div + div {
    margin-top: ${tokens.space['4']};
  }
`;

const DetailRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const DetailLabel = styled.dt`
  margin: 0;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
`;

const DetailValue = styled.dd`
  margin: 0 0 0 ${tokens.space['12']};
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
  text-align: right;
`;

/* The reason is NOT one of the label-left / value-right rows above. 3583-7541 stacks it: Governing
   reason on its own line, then the reason itself beneath, left-aligned, inked in the pill's own hue
   and led by a tone dot. The two dated rows keep the split row the lifecycle card draws them in
   (3583-10341). PLA-8828. */
const ReasonBlock = styled.div`
  > * + * {
    margin-top: ${tokens.space['4']};
  }
`;

const ReasonLabel = styled.p`
  margin: 0;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
`;

/* The dot follows the tone-dot treatment RiskEvidenceSection already draws before a band word
   (3583-9195); no geometry table measures this one, so it reuses that size rather than inventing a
   reading of its own. */
const ReasonValue = styled.p<{ $hue: string }>`
  display: flex;
  align-items: center;
  margin: 0;
  color: ${({ $hue }) => $hue};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['label-line']};

  &::before {
    display: inline-block;
    width: 8px;
    height: 8px;
    flex: 0 0 auto;
    margin-right: ${tokens.space['8']};
    border-radius: ${tokens.radius.pill};
    background: currentColor;
    content: '';
  }
`;

const StatusBadgeWithReason: React.FC<IStatusBadgeWithReasonProps> = ({
  label,
  labelCase = 'upper',
  status,
  tone = DEFAULT_TONE,
  color,
  effective,
  prior,
  reason,
  className
}) => {
  const details = [effective, prior].filter(Boolean) as IStatusBadgeDetail[];
  const hue = color || STATUS_PILL_HUES[tone];

  return (
    <Wrapper className={className} data-tone={tone} data-testid="status-badge-with-reason">
      {label ? (
        <FieldLabel $sentence={labelCase === 'sentence'} data-testid="status-badge-label">
          {label}
        </FieldLabel>
      ) : null}

      {/* The pill's own word carries the state, so the tone hue only reinforces it and no reading
          depends on colour (WCAG 1.4.1). */}
      <Pill $hue={hue} data-testid="status-badge-pill">
        <PillLabel>{status}</PillLabel>
      </Pill>

      {details.length > 0 ? (
        <DetailList data-testid="status-badge-details">
          {details.map(detail => (
            <DetailRow key={detail.label}>
              <DetailLabel>{detail.label}</DetailLabel>
              <DetailValue>{detail.value}</DetailValue>
            </DetailRow>
          ))}
        </DetailList>
      ) : null}

      {reason ? (
        <ReasonBlock data-testid="status-badge-reason">
          <ReasonLabel data-testid="status-badge-reason-label">{reason.label}</ReasonLabel>
          {/* The dot is decoration on a line whose own words carry the reason (WCAG 1.4.1). */}
          <ReasonValue $hue={hue} data-testid="status-badge-reason-value">
            {reason.value}
          </ReasonValue>
        </ReasonBlock>
      ) : null}
    </Wrapper>
  );
};

export default StatusBadgeWithReason;
